"use client";

import { useCallback, useMemo, useState } from "react";
import type { ReactElement, RefObject } from "react";
import { KanbanCompactPreview } from "./KanbanCompactPreview";
import { getKanbanNavigationControls } from "./KanbanNavigationControls";
import { parseMermaidKanban, type ParsedKanban } from "./parse-mermaid-kanban";
import {
  getKanbanCardKeys,
  getTotalCards,
} from "./kanban-selectors";
import {
  type ScrollHintState,
  type SetColumnRefInput,
  useKanbanBoardScroll,
} from "./useKanbanBoardScroll";

interface UseCmarkdownkanbanControllerInput {
  content: string;
}

export interface CmarkdownkanbanController {
  parsed: ParsedKanban;
  totalCards: number;
  compactPreview: ReactElement;
  controls?: ReactElement;
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  scrollHintState: ScrollHintState;
  activeHoveredCardKey: string | null;
  setColumnRef: (input: SetColumnRefInput) => void;
  onHoverCard: (cardKey: string) => void;
  clearHoveredCard: () => void;
}

export function useCmarkdownkanbanController({
  content,
}: UseCmarkdownkanbanControllerInput): CmarkdownkanbanController | null {
  const [hoveredCardKey, setHoveredCardKey] = useState<string | null>(null);
  const parsed = useMemo(() => parseMermaidKanban(content), [content]);
  const cardKeys = useMemo(() => getKanbanCardKeys(parsed), [parsed]);
  const clearHoveredCard = useCallback((): void => {
    setHoveredCardKey((previousHoveredCardKey) => {
      return previousHoveredCardKey == null ? previousHoveredCardKey : null;
    });
  }, []);
  const columnCount = getKanbanColumnCount({ parsed });
  const {
    scrollContainerRef,
    scrollHintState,
    setColumnRef,
    handleScrollToPrevColumn,
    handleScrollToNextColumn,
  } = useKanbanBoardScroll({
    columnCount,
    clearHoveredCard,
  });
  const activeHoveredCardKey = getActiveKanbanCardKey({
    hoveredCardKey,
    cardKeys,
  });
  const totalCards = useMemo(() => getTotalCards(parsed), [parsed]);
  const compactPreview = useMemo(
    () => getKanbanCompactPreview({ parsed }),
    [parsed],
  );

  if (!parsed || !compactPreview) {
    return null;
  }

  return {
    parsed,
    totalCards,
    compactPreview,
    controls: getKanbanNavigationControls({
      scrollHintState,
      handleScrollToPrevColumn,
      handleScrollToNextColumn,
    }),
    scrollContainerRef,
    scrollHintState,
    activeHoveredCardKey,
    setColumnRef,
    onHoverCard: setHoveredCardKey,
    clearHoveredCard,
  };
}

function getKanbanColumnCount({
  parsed,
}: {
  parsed: ParsedKanban | null;
}): number {
  return parsed?.columns.length ?? 0;
}

function getActiveKanbanCardKey({
  hoveredCardKey,
  cardKeys,
}: {
  hoveredCardKey: string | null;
  cardKeys: Set<string>;
}): string | null {
  if (!hoveredCardKey || !cardKeys.has(hoveredCardKey)) {
    return null;
  }

  return hoveredCardKey;
}

function getKanbanCompactPreview({
  parsed,
}: {
  parsed: ParsedKanban | null;
}): ReactElement | null {
  if (!parsed || parsed.columns.length === 0) {
    return null;
  }

  return <KanbanCompactPreview parsed={parsed} />;
}
